<template>
  <v-dialog v-model="dialogModel" content-class="haq-dialog" max-width="1200px" @click:outside="closeModal">
    <v-card class="haq-dialog-card" style="min-width: 1000px; min-height: 400px;">
      <v-card-title class="haq-dialog-card-header">
        <h5>{{ measurement ? measurement.name : 'Measurement Details' }}</h5>
        <v-spacer></v-spacer>
        <v-icon class="close-icon" text="text" @click="closeModal">mdi-close</v-icon>
      </v-card-title>
      <v-card-text style="display: flex; flex-direction: column; gap: 0; padding: 24px;">
        <!-- Measurement Info Section -->
        <div class="measurement-info-section" v-if="measurement">
          <div class="measurement-header">
            <!-- <div class="measurement-title">
              <h3>{{ measurement.name }}</h3>
              <div class="measurement-meta">
                <span class="measurement-type">{{ getMeasurementTypeDisplay(measurement.type) }}</span>
                <span class="measurement-unit" v-if="measurement.unit">{{ measurement.unit }}</span>
              </div>
            </div> -->
            <div class="measurement-value">
              <div class="current-value">
                <span class="value-number">{{ getCurrentValue(measurement) }}</span>
                <span class="value-unit" v-if="measurement.unit">{{ measurement.unit }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Chart Section -->
        <div class="chart-section" v-if="measurement && measurement.chartData">
          <div class="chart-container">
            <AreaChartLarge 
              :dataArray="measurement.chartData.data"
              :colorMode="String(measurement.chartData.colorMode)"
              :units="measurement.unit"
              :range="measurement.chartData.range"
              :enumeration="measurement.chartData.enumeration"
              :multiStateValues="measurement.chartData.multiStateValues"
              height="400px">
            </AreaChartLarge>
          </div>
        </div>

        <!-- Loading State -->
        <div class="loading-section" v-if="!measurement || !measurement.chartData">
          <div class="loading-container">
            <v-progress-circular indeterminate="indeterminate" color="var(--score-blue-color)" size="48"></v-progress-circular>
            <span class="loading-text">Loading measurement data...</span>
          </div>
        </div>
      </v-card-text>
      <v-card-actions class="mt-auto ml-auto">
        <v-spacer></v-spacer>
        <v-btn class="haq-btn haq-btn-secondary" text @click="closeModal">CLOSE</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import AreaChartLarge from '../AreaChartLarge'

export default {
  name: 'DashboardAqDetailModal',
  components: {
    AreaChartLarge
  },
  props: {
    dialog: {
      type: Boolean,
      default: false
    },
    measurement: {
      type: Object,
      default: null
    }
  },
  computed: {
    dialogModel: {
      get() {
        return this.dialog;
      },
      set(value) {
        this.$emit('update:dialog', value);
      }
    }
  },
  methods: {
    closeModal() {
      this.$emit('update:dialog', false);
      this.$emit('close');
    },
    getMeasurementTypeDisplay(type) {
      if (!type) return 'Unknown';
      
      // Convert type to display format
      const typeMap = {
        'scoreOA': 'Overall Score',
        'scoreAQ': 'Air Quality Score',
        'scoreOW': 'Occupant Wellness Score',
        'scoreAF': 'Air Freshness Score',
        'scoreEQ': 'Equipment Score',
        'temperature': 'Temperature',
        'relativehumidity': 'Relative Humidity',
        'co2': 'CO₂',
        'pm2.5': 'PM2.5',
        'pm10': 'PM10',
        'totalvocs': 'Total VOCs',
        'voc': 'VOCs',
        'ozone': 'Ozone',
        'carbonmonoxide': 'Carbon Monoxide',
        'airquality': 'Air Quality',
        'ventilation': 'Ventilation',
        'ionization': 'Ionization',
        'ionizerstatus': 'Ionizer Status',
        'ionlevel': 'Ion Level',
        'activepure': 'ActivePure',
        'timeinactivepure': 'Time in ActivePure',
        'timeincomfortzone': 'Time in Comfort Zone',
        'infectiontransmissibility': 'Infection Transmissibility',
        'spacetemperature': 'Space Temperature'
      };
      
      return typeMap[type] || type.charAt(0).toUpperCase() + type.slice(1);
    },
    getCurrentValue(measurement) {
      if (!measurement) return '--';
      
      // Prefer curVal, fallback to curScore
      if (measurement.curVal !== undefined && measurement.curVal !== null) {
        if (!isNaN(measurement.curVal)) {
          return Math.round(measurement.curVal * 100) / 100;
        }
        return measurement.curVal;
      }
      
      if (measurement.curScore !== undefined && measurement.curScore !== null) {
        return Math.round(measurement.curScore);
      }
      
      // Try to get from chart data
      if (measurement.chartData && measurement.chartData.data && measurement.chartData.data.length > 0) {
        const lastValue = measurement.chartData.data[measurement.chartData.data.length - 1][1];
        if (!isNaN(lastValue)) {
          return Math.round(lastValue * 100) / 100;
        }
        return lastValue;
      }
      
      return '--';
    }
  }
}
</script>

<style lang="sass" scoped>
// Modal styling to match haq-dialog patterns
.haq-dialog-card
  background: var(--app-tertiary-bg) !important
  border-radius: 20px !important
  overflow: hidden
  
  .haq-dialog-card-header
    background: var(--app-secondary-bg)
    border-bottom: 2px solid rgba(125, 133, 197, 0.2)
    padding: 24px 24px 20px 24px
    display: flex
    align-items: center
    justify-content: space-between
    
    h5
      color: var(--app-primary-txt)
      font: normal 400 24px/28px 'Roboto', sans-serif
      margin: 0
    
    .close-icon
      color: var(--app-tertiary-txt)
      cursor: pointer
      transition: color 0.2s ease
      
      &:hover
        color: var(--app-primary-txt)
  
  .v-card__text
    padding: 24px
    background: var(--app-tertiary-bg)
    min-height: 500px
    
  .v-card__actions
    background: var(--app-secondary-bg)
    padding: 20px 24px
    border-top: 1px solid var(--border)
    
    .haq-btn
      border-radius: 12px !important
      padding: 8px 24px !important
      font-weight: 500 !important
      text-transform: none !important
      min-width: 100px !important
      height: 2.8em !important
      line-height: 2.8em !important
      
      &.haq-btn-primary
        background: var(--score-blue-color) !important
        color: white !important
        
        &:hover
          background: var(--score-blue-color) !important
          opacity: 0.9 !important
          
        &:disabled
          background: var(--app-tertiary-bg) !important
          color: var(--app-tertiary-txt) !important
          opacity: 0.6 !important
      
      &.haq-btn-secondary
        background: var(--app-tertiary-bg) !important
        color: var(--app-primary-txt) !important
        border: 1px solid var(--border) !important
        
        .v-btn__content
          color: var(--app-primary-txt) !important
        
        &:hover
          background: var(--app-secondary-bg) !important

// Measurement info section
.measurement-info-section
  margin-bottom: 24px
  
  .measurement-header
    display: flex
    justify-content: space-between
    align-items: flex-start
    gap: 24px
    
    .measurement-title
      flex: 1
      
      h3
        color: var(--app-primary-txt)
        font-size: 1.5rem
        font-weight: 500
        margin: 0 0 8px 0
        
      .measurement-meta
        display: flex
        gap: 16px
        align-items: center
        
        .measurement-type
          color: var(--score-blue-color)
          font-size: 0.9rem
          font-weight: 500
          background: rgba(78, 124, 255, 0.1)
          padding: 4px 12px
          border-radius: 16px
          
        .measurement-unit
          color: var(--app-tertiary-txt)
          font-size: 0.9rem
    
    .measurement-value
      text-align: right
      
      .current-value
        display: flex
        align-items: baseline
        justify-content: flex-end
        gap: 4px
        
        .value-number
          color: var(--app-primary-txt)
          font-size: 2.5rem
          font-weight: 300
          line-height: 1
          
        .value-unit
          color: var(--app-tertiary-txt)
          font-size: 1rem
          font-weight: 300
          margin-left: 8px
          opacity: .7
          
      .value-label
        color: var(--app-tertiary-txt)
        font-size: 0.8rem
        margin-top: 4px

// Chart section
.chart-section
  flex: 1
  min-height: 400px
  
  .chart-container
    width: 100%
    height: 100%
    background: var(--app-secondary-bg)
    border-radius: 12px
    padding: 16px
    border: 1px solid var(--border)

// Loading section
.loading-section
  flex: 1
  display: flex
  align-items: center
  justify-content: center
  min-height: 400px
  
  .loading-container
    display: flex
    flex-direction: column
    align-items: center
    gap: 16px
    
    .loading-text
      color: var(--app-tertiary-txt)
      font-size: 1rem

// Responsive adjustments
@media (max-width: 768px)
  .measurement-header
    flex-direction: column
    align-items: flex-start
    gap: 16px
    
    .measurement-value
      text-align: left
      
      .current-value
        justify-content: flex-start
        
  .chart-section
    min-height: 300px
</style> 